/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import type { ShopperProducts } from '@/scapi';
import ProductPrice from '@/components/product-price';
import PromoCallout from '@/components/product-price/promo-callout';
import ProductQuantityPicker from '@/components/product-quantity-picker';
import CollapsibleHtmlSection from '@/components/collapsible-section/collapsible-html-section';
import { Button } from '@/components/ui/button';
import { UITarget } from '@/targets/ui-target';
// @sfdc-extension-block-start SFDC_EXT_BOPIS
// DeliveryOptions rendering is temporarily disabled below — import commented out to match.
// import DeliveryOptions from '@/extensions/bopis/components/delivery-options/delivery-options';
import type { SelectedStoreInfo } from '@/extensions/store-locator/stores/store-locator-store';
// @sfdc-extension-block-end SFDC_EXT_BOPIS

interface ProductBundleDetailsProps {
    product: ShopperProducts.schemas['Product'];
    selectedBundleQuantity: number;
    setSelectedBundleQuantity: (quantity: number) => void;
    bundleStockLevel?: number;
    bundleOutOfStock?: boolean;
    canAddToCart: boolean;
    isAddingToOrUpdatingCart: boolean;
    onAddToCart: () => void;
    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    productWithCalculatedInventory: ShopperProducts.schemas['Product'];
    effectiveQuantity: number;
    basketPickupStore?: SelectedStoreInfo;
    // @sfdc-extension-block-end SFDC_EXT_BOPIS
}

/**
 * Right/left detail columns for the Product Bundle Page: title, item number, bundle price and
 * description/product-details accordions on the left; promo note, quantity stepper, and add-to-cart on the
 * right. Unlike `ChildProducts` (used for sets and "build your own" bundles), this has no per-child variant
 * picker — bundle children here are fixed, standard products that `useProductSetsBundles` auto-selects, so
 * add-to-cart is gated purely on availability.
 */
export default function ProductBundleDetails({
    product,
    selectedBundleQuantity,
    setSelectedBundleQuantity,
    bundleStockLevel,
    bundleOutOfStock,
    canAddToCart,
    isAddingToOrUpdatingCart,
    onAddToCart,
    // @sfdc-extension-line SFDC_EXT_BOPIS
    productWithCalculatedInventory: _productWithCalculatedInventory,
    // @sfdc-extension-line SFDC_EXT_BOPIS
    effectiveQuantity: _effectiveQuantity,
    // @sfdc-extension-line SFDC_EXT_BOPIS
    basketPickupStore: _basketPickupStore,
}: ProductBundleDetailsProps): ReactElement {
    const { t } = useTranslation('product');
    const { currency } = useSite();

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12">
            {/* Left column: title, item number, price, accordions */}
            <div className="space-y-4">
                <div>
                    <h1 data-testid="product-title" className="text-3xl font-bold text-card-foreground tracking-tight">
                        {product.name}
                    </h1>
                    {product.id && (
                        <p className="mt-2 text-xs leading-none text-secondary-foreground">
                            {t('itemNumber')} {product.id}
                        </p>
                    )}
                </div>

                <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium text-card-foreground">{t('allItemsFor')}</span>
                    <ProductPrice
                        type="unit"
                        product={product}
                        currency={currency}
                        labelForA11y={product.name}
                        hidePromo
                        currentPriceProps={{
                            className: 'text-2xl font-normal text-card-foreground leading-[120%] tracking-[-0.6px]',
                        }}
                    />
                </div>

                <div className="space-y-3">
                    {product.longDescription && product.longDescription !== product.shortDescription && (
                        <CollapsibleHtmlSection
                            label={`${t('description')}:`}
                            content={product.longDescription}
                            contentType="plain-text"
                            variant="pdp"
                        />
                    )}
                    <UITarget targetId="sfcc.pdp.collapsibles" />
                </div>
            </div>

            {/* Right column: promo note, quantity, add to cart */}
            <div className="space-y-4 flex flex-col items-center ">
                <PromoCallout product={product} className="text-base text-primary" />

                <ProductQuantityPicker
                    value={selectedBundleQuantity.toString()}
                    onChange={setSelectedBundleQuantity}
                    stockLevel={bundleStockLevel}
                    isOutOfStock={bundleOutOfStock}
                    productName={product.name}
                    isBundle
                    className="max-w-100"
                />

                {/* @sfdc-extension-block-start SFDC_EXT_BOPIS */}
                {/*  <DeliveryOptions
                    product={productWithCalculatedInventory}
                    quantity={effectiveQuantity}
                    basketPickupStore={basketPickupStore}
                /> */}
                {/* @sfdc-extension-block-end SFDC_EXT_BOPIS */}

                <Button
                    data-testid="add-to-cart"
                    onClick={onAddToCart}
                    disabled={!canAddToCart || isAddingToOrUpdatingCart}
                    size="lg"
                    className="w-full max-w-100">
                    {isAddingToOrUpdatingCart ? t('adding') : t('addBundleToCart')}
                </Button>
            </div>
        </div>
    );
}
